import { PremiumApiResponse, LaravelPage, responseData, responsePage } from './premium-api-response';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, map } from 'rxjs';
import { ApiService } from '@core/services/api';
import { toHttpParams } from '@core/services/api/interfaces/query-params';
import {
  ApiPage,
  AutoRecharge,
  CreateTopup,
  CreditActor,
  CreditEntry,
  CreditSummary,
  StorageBillingSummary,
  CreditTopup,
} from './governance-premium.models';

function nonNegativeInteger(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0) throw new Error('Saldo indisponível.');
  return parsed;
}

@Injectable({ providedIn: 'root' })
export class CreditsService {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly balanceChanged = new Subject<void>();
  readonly balanceChanges = this.balanceChanged.asObservable();

  private get url(): string {
    return `${this.api.getApiUrl()}/credits`;
  }

  summary(): Observable<CreditSummary> {
    return this.http.get<PremiumApiResponse<CreditSummary>>(this.url).pipe(map(responseData)).pipe(map(summary => ({
      ...summary,
      balance: nonNegativeInteger(summary.balance),
      reserved: nonNegativeInteger(summary.reserved),
      available: nonNegativeInteger(summary.available),
      bonus_remaining: nonNegativeInteger(summary.bonus_remaining),
      recharge_remaining: nonNegativeInteger(summary.recharge_remaining),
      tariffs: Array.isArray(summary.tariffs) ? summary.tariffs : [],
    })));
  }

  storageConsent(enabled: boolean, tariffVersion: string): Observable<StorageBillingSummary> {
    return this.http.put<PremiumApiResponse<StorageBillingSummary>>(`${this.url}/storage-consent`, {
      enabled, tariff_version: tariffVersion,
    }).pipe(map(responseData));
  }

  entries(params: Record<string, unknown> = {}): Observable<ApiPage<CreditEntry>> {
    const compact = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== '' && value != null));
    return this.http.get<PremiumApiResponse<LaravelPage<CreditEntry>>>(`${this.url}/entries`, { params: toHttpParams(compact) }).pipe(map(responsePage));
  }

  actors(): Observable<CreditActor[]> {
    return this.http.get<PremiumApiResponse<CreditActor[]>>(`${this.url}/actors`).pipe(map(responseData));
  }

  quote(service: string, quantity: number): Observable<{
    service: string; quantity: number; unit_credits: number; total_credits: number;
    available: number; sufficient: boolean; tariff_version: string;
  }> {
    return this.http.post<PremiumApiResponse<{
      service: string; quantity: number; unit_credits: number; total_credits: number;
      available: number; sufficient: boolean; tariff_version: string;
    }>>(`${this.url}/quote`, { service, quantity }).pipe(map(responseData));
  }

  topups(params: { idempotency_key?: string; page?: number; per_page?: number; search?: string; status?: string; instrument?: string } = {}): Observable<ApiPage<CreditTopup>> {
    return this.http.get<PremiumApiResponse<LaravelPage<CreditTopup>>>(`${this.url}/topups`, {
      params: toHttpParams(params),
    }).pipe(map(responsePage));
  }

  createTopup(payload: CreateTopup): Observable<CreditTopup> {
    return this.http.post<PremiumApiResponse<CreditTopup>>(`${this.url}/topups`, payload).pipe(map(responseData));
  }

  cardConfiguration(): Observable<{ credential: string; sandbox: boolean }> {
    return this.http.get<PremiumApiResponse<{ credential: string; sandbox: boolean }>>(`${this.url}/card-configuration`).pipe(map(responseData));
  }

  topup(id: string): Observable<CreditTopup> {
    return this.http.get<PremiumApiResponse<CreditTopup>>(`${this.url}/topups/${encodeURIComponent(id)}`).pipe(map(responseData));
  }

  autoRecharge(): Observable<AutoRecharge> {
    return this.http.get<PremiumApiResponse<AutoRecharge>>(`${this.url}/auto-recharge`).pipe(map(responseData));
  }

  updateAutoRecharge(payload: AutoRecharge): Observable<AutoRecharge> {
    return this.http.put<PremiumApiResponse<AutoRecharge>>(`${this.url}/auto-recharge`, payload).pipe(map(responseData));
  }

  notifyBalanceChanged(): void {
    this.balanceChanged.next();
  }
}
